// Import contract (Ability Format v2, P4b) — the wire shape shared by the
// server import endpoint, sandbox import tools, and the web import hook.
// Source kinds widen over time (upload/inline arrive in later plans); the
// schema is `.strict()` on the source wrapper so an unrecognized kind is
// rejected with a parse error instead of silently passing through.

import { z } from 'zod';
import type { AbilityReloadPayloadSchema } from '../schemas/runner-protocol.schema.js';

/**
 * A GitHub repo (or subpath within it) to import parts from.
 *
 * `ref`/`subpath` are typed `| undefined` (not just `?:`) because this
 * package builds with `exactOptionalPropertyTypes`, under which zod's
 * `.optional()` output is `T | undefined`, not merely an omittable key.
 * Behaviorally identical to plain `?:` for every consumer.
 */
export interface GithubImportSource {
  url: string;
  ref?: string | undefined;
  subpath?: string | undefined;
}

/** Where an import pulls content from. Only `github` exists in P4b. */
export type ImportSource = { github: GithubImportSource };

/**
 * Where imported parts land: an existing owned ability by slug, or a new
 * ability to create. 'my-kit' is the default target when omitted.
 */
export type ImportTarget =
  | { ability: string }
  | { newAbility: { slug: string; name: string } };

export interface ImportRequest {
  source: ImportSource;
  target?: ImportTarget | undefined;
  /** Part keys in `<kind>:<slug>` shape (see {@link partKeyOf}). */
  select?: string[] | undefined;
  dryRun?: boolean | undefined;
  /**
   * X4: the `confirm` hash of the plugin plan the person saw (from a dry run or a
   * `plugin_confirm_required` reply). A plugin import without the current plan's hash writes nothing.
   */
  confirm?: string | undefined;
}

/** One part discovered in the import source, before it's imported. */
export interface DetectedPart {
  kind: 'skill' | 'agent';
  /** The slug it will be saved under: a skill's frontmatter `name`, else its folder's. */
  slug: string;
  /** `partKeyOf` shape over its folder: `<kind>:<folder slug>`. What `select` takes. */
  key: string;
  name?: string;
  description?: string;
  /** 1 for agents; SKILL.md + bundle file count for skills. */
  fileCount: number;
}

/** One part actually imported into the target ability. */
export interface ImportedPart {
  kind: 'skill' | 'agent';
  slug: string;
  id: string;
  /** The {@link DetectedPart.key} it was written from. */
  key: string;
  /** The slug it would have had, when another of the person's items already held it. */
  renamedFrom?: string;
  /** A re-import found the row this part was imported into edited by the person since: it was kept as they left it, and this part saved beside it. */
  editedSinceImport?: true;
  /** It updated, in place, the row an earlier import of this same part wrote. */
  updated?: true;
  /** A row already held exactly this part (an earlier import of it, or the same content): nothing was written. */
  unchanged?: true;
}

/**
 * X4 (PLUGIN_IMPORT_V1): one part of an imported plugin and what became of it. A plugin imports as
 * one ability, whole: every part it declares is listed here, imported or not, so nothing is ever
 * dropped silently.
 */
export interface PluginPartReport {
  /** A command imports as a skill; hooks are one entry for the whole plugin. */
  kind: 'skill' | 'command' | 'agent' | 'mcp' | 'hooks';
  name: string;
  /** `inert`: imported and kept on the ability, but switched off (hooks, until they can run safely). */
  status: 'imported' | 'inert' | 'skipped';
  /** Why it was skipped or what being inert means, in plain words for the person. */
  note?: string;
  /** An MCP server's host: where it connects, shown before the person confirms. */
  host?: string;
  /** The secrets an MCP server needs (named for this plugin and server), shown before the person confirms. */
  secrets?: string[];
}

/**
 * X4: the reply to a plugin import without the current plan's `confirm` hash (HTTP 409): the plan, part
 * by part, and the hash to send back once the person confirms it.
 */
export interface PluginConfirmRequired {
  error: 'plugin_confirm_required';
  message: string;
  plugin: PluginImportReport;
  /** The person's confirm token for this plan (short-lived, theirs alone). Never sent to the box. */
  confirm: string;
}

/** X4: what a plugin import did, part by part. */
export interface PluginImportReport {
  name: string;
  version?: string;
  description?: string;
  parts: PluginPartReport[];
}

export type ImportResponse =
  | { ok: true; dryRun: true; detected: DetectedPart[]; plugin?: PluginImportReport; confirm?: string }
  | { ok: true; requiresSelection: true; detected: DetectedPart[] }
  | { ok: true; abilityId: string; abilitySlug: string; imported: ImportedPart[]; pressingHash: string | null; plugin?: PluginImportReport };

const GithubImportSourceSchema = z.object({
  url: z.string(),
  ref: z.string().optional(),
  subpath: z.string().optional(),
});

const ImportSourceSchema = z
  .object({ github: GithubImportSourceSchema })
  .strict();

const ImportTargetSchema = z.union([
  z.object({ ability: z.string() }).strict(),
  z.object({ newAbility: z.object({ slug: z.string(), name: z.string() }) }).strict(),
]);

export const ImportRequestSchema: z.ZodType<ImportRequest> = z.object({
  source: ImportSourceSchema,
  target: ImportTargetSchema.optional(),
  select: z.array(z.string()).optional(),
  dryRun: z.boolean().optional(),
  confirm: z.string().max(200).optional(),
});

/** An upload import's options (POST /api/items/import/upload, field `options`, JSON beside the zip): a GitHub import's, less its source. */
export interface UploadImportOptions {
  target?: ImportTarget | undefined;
  select?: string[] | undefined;
  dryRun?: boolean | undefined;
}

export const UploadImportOptionsSchema: z.ZodType<UploadImportOptions> = z
  .object({
    target: ImportTargetSchema.optional(),
    select: z.array(z.string()).optional(),
    dryRun: z.boolean().optional(),
  })
  .strict();

/**
 * Ability-reload push (server -> sandbox), payload for `EVENTS.ABILITY_RELOAD`.
 * Optional so old handlers that take no payload arg keep working untouched.
 */
export type AbilityReloadPayload = z.infer<typeof AbilityReloadPayloadSchema>;
